import React, { useState, useEffect, useRef } from "react";
import {
  MemberRegistrationForm,
  MembershipType,
  Gender,
  ShirtSize,
} from "../../types";
import { isUnderAge, calculateAge } from "../../utils/membership";
import { clubYear, earliestJuniorDob } from "@shared/membership";
import { formatClubDate } from "../../utils/clubTime";
import DateOfBirthSelect from "../ui/DateOfBirthSelect";
import {
  debouncedAddressAutocomplete,
  cancelPendingAutocomplete,
  AddressSuggestion,
  getPlaceDetails,
  parseAustralianAddress,
  looksLikeSubpremise,
} from "../../utils/addressAutocomplete";
import { toTitleCase } from "../../utils/textNormalization";
import { logDebug } from '../../utils/logger';

interface PersonalDetailsStepProps {
  formData: MemberRegistrationForm;
  updateFormData: (updates: Partial<MemberRegistrationForm>) => void;
  onNext: () => void;
  onCancel?: () => void;
}

const PersonalDetailsStep: React.FC<PersonalDetailsStepProps> = ({
  formData,
  updateFormData,
  onNext,
  onCancel,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [addressSuggestions, setAddressSuggestions] = useState<
    AddressSuggestion[]
  >([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelPendingAutocomplete();
    };
  }, []);

  // Handle clicks outside suggestions dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        addressInputRef.current &&
        !addressInputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAddressChange = (value: string) => {
    updateFormData({ address: value });

    // Only trigger autocomplete if we have at least 3 characters
    if (value.length >= 3) {
      setIsLoadingSuggestions(true);
      setShowSuggestions(true);

      debouncedAddressAutocomplete(value, (suggestions) => {
        setAddressSuggestions(suggestions);
        setIsLoadingSuggestions(false);
      });
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      setIsLoadingSuggestions(false);
    }
  };

  const handleSuggestionSelect = async (suggestion: AddressSuggestion) => {
    setShowSuggestions(false);
    setAddressSuggestions([]);

    // If we have a placeId, get detailed place information
    if (suggestion.placeId) {
      logDebug('PersonalDetailsStep', "Getting place details for placeId:", suggestion.placeId);
      const parsedAddress = await getPlaceDetails(suggestion.placeId);
      logDebug('PersonalDetailsStep', "Parsed address:", parsedAddress);

      if (parsedAddress) {
        // Populate all four address fields
        logDebug('PersonalDetailsStep', "Updating form with:", {
          address: parsedAddress.streetAddress,
          suburb: parsedAddress.suburb,
          state: parsedAddress.state,
          postCode: parsedAddress.postCode,
        });
        updateFormData({
          address: parsedAddress.streetAddress,
          suburb: parsedAddress.suburb,
          state: parsedAddress.state,
          postCode: parsedAddress.postCode,
        });
        return;
      }
    }

    // Fallback: parse the suggestion text client-side
    logDebug('PersonalDetailsStep', "Fallback: parsing suggestion text:", suggestion.text);
    const parsed = parseAustralianAddress(suggestion.text);
    if (parsed) {
      logDebug('PersonalDetailsStep', "Parsed from text:", parsed);
      updateFormData({
        address: parsed.streetAddress,
        suburb: parsed.suburb,
        state: parsed.state,
        postCode: parsed.postCode,
      });
      return;
    }

    // Last resort: just use the suggestion text in the address field
    logDebug('PersonalDetailsStep', "Last resort: using raw suggestion text:", suggestion.text);
    updateFormData({ address: suggestion.text });
  };

  const handleClearAddress = () => {
    updateFormData({ address: "" });
    setAddressSuggestions([]);
    setShowSuggestions(false);
    addressInputRef.current?.focus();
  };

  const NAME_PATTERN = /^[A-Za-zÀ-ÿ'’\- ]+$/;
  const AU_PHONE_PATTERN = /^(?:\+?61\s?|0)[2-478](?:[ -]?[0-9]){8}$/;

  type ValidatableField =
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'phoneNumber'
    | 'suburb'
    | 'state'
    | 'postCode';

  const validateField = (field: ValidatableField): string | null => {
    const v = (formData[field] || '').toString().trim();
    if (field === 'firstName' || field === 'lastName') {
      if (!v) return `${field === 'firstName' ? 'First' : 'Last'} name is required`;
      if (v.length < 2) return 'Must be at least 2 characters';
      if (!NAME_PATTERN.test(v)) return 'Letters, spaces, hyphens and apostrophes only';
      return null;
    }
    if (field === 'email') {
      if (!v) return 'Email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Invalid email format';
      return null;
    }
    if (field === 'phoneNumber') {
      if (!v) return 'Phone number is required';
      if (!AU_PHONE_PATTERN.test(v)) return 'Enter an Australian number (e.g. 04xx xxx xxx)';
      return null;
    }
    if (field === 'suburb') {
      if (!v) return 'Suburb is required';
      if (looksLikeSubpremise(v))
        return 'This looks like a unit number — enter the suburb (e.g. Tumbulgum)';
      return null;
    }
    if (field === 'state') {
      if (!v) return 'State is required';
      return null;
    }
    if (field === 'postCode') {
      if (!v) return 'Post code is required';
      if (!/^\d{4}$/.test(v)) return 'Enter a 4-digit post code';
      return null;
    }
    return null;
  };

  const handleBlur = (field: ValidatableField) => {
    const msg = validateField(field);
    setErrors((prev) => {
      const next = { ...prev };
      if (msg) next[field] = msg;
      else delete next[field];
      return next;
    });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    const fn = validateField('firstName');
    if (fn) newErrors.firstName = fn;
    const ln = validateField('lastName');
    if (ln) newErrors.lastName = ln;
    const em = validateField('email');
    if (em) newErrors.email = em;
    const ph = validateField('phoneNumber');
    if (ph) newErrors.phoneNumber = ph;
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = "Date of birth is required";
    } else {
      const age = calculateAge(formData.dateOfBirth);
      const year = clubYear();
      const earliestJuniorDobWords = formatClubDate(earliestJuniorDob(year), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });

      if (formData.membershipType === MembershipType.JUNIOR) {
        if (!isUnderAge(formData.dateOfBirth, new Date())) {
          newErrors.dateOfBirth = `Junior members must be born on or after ${earliestJuniorDobWords}.`;
        } else {
          updateFormData({ age });
        }
      } else {
        if (isUnderAge(formData.dateOfBirth, new Date())) {
          newErrors.dateOfBirth = `Juniors (born on or after ${earliestJuniorDobWords}) join as a Junior member or as part of a family.`;
        } else {
          updateFormData({ age });
        }
      }
    }
    if (!formData.phoneNumber.trim()) {
      // handled by validateField above
    }
    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }
    const su = validateField('suburb');
    if (su) newErrors.suburb = su;
    const st = validateField('state');
    if (st) newErrors.state = st;
    const pc = validateField('postCode');
    if (pc) newErrors.postCode = pc;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">
        Personal Details
      </h2>
      <p className="text-gray-600 mb-6">
        Please provide your personal information
      </p>

      <div className="space-y-4">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => updateFormData({ firstName: toTitleCase(e.target.value) })}
              onBlur={() => handleBlur('firstName')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
                errors.firstName ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="John"
            />
            {errors.firstName && (
              <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => updateFormData({ lastName: toTitleCase(e.target.value) })}
              onBlur={() => handleBlur('lastName')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
                errors.lastName ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Smith"
            />
            {errors.lastName && (
              <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => updateFormData({ email: e.target.value })}
            onBlur={() => handleBlur('email')}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
              errors.email ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="john.smith@example.com"
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
          )}
        </div>

        {/* Date of Birth and Gender */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <DateOfBirthSelect
              value={formData.dateOfBirth}
              onChange={(v) => updateFormData({ dateOfBirth: v })}
              error={!!errors.dateOfBirth}
              idPrefix="personal-dob"
            />
            {errors.dateOfBirth && (
              <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.gender}
              onChange={(e) =>
                updateFormData({ gender: e.target.value as Gender })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent"
            >
              <option value={Gender.MALE}>Male</option>
              <option value={Gender.FEMALE}>Female</option>
              <option value={Gender.OTHER}>Other</option>
              <option value={Gender.PREFER_NOT_TO_SAY}>
                Prefer not to say
              </option>
            </select>
          </div>
        </div>

        {/* Phone and Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={formData.phoneNumber}
            onChange={(e) => updateFormData({ phoneNumber: e.target.value })}
            onBlur={() => handleBlur('phoneNumber')}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
              errors.phoneNumber ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="0412 345 678"
          />
          {errors.phoneNumber && (
            <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>
          )}
        </div>

        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Street Address <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              ref={addressInputRef}
              type="text"
              value={formData.address}
              onChange={(e) => handleAddressChange(e.target.value)}
              onFocus={() =>
                formData.address.length >= 3 &&
                addressSuggestions.length > 0 &&
                setShowSuggestions(true)
              }
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
                errors.address ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Start typing your address... (e.g., 123 Main St)"
            />
            {formData.address && (
              <button
                type="button"
                onClick={handleClearAddress}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear address"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Autocomplete Suggestions Dropdown */}
          {showSuggestions && (
            <div
              ref={suggestionsRef}
              className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            >
              {isLoadingSuggestions ? (
                <div className="px-4 py-3 text-sm text-gray-500 flex items-center">
                  <svg
                    className="animate-spin h-4 w-4 mr-2 text-fishing-blue"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading suggestions...
                </div>
              ) : addressSuggestions.length > 0 ? (
                <ul>
                  {addressSuggestions.map((suggestion, index) => (
                    <li key={index}>
                      <button
                        type="button"
                        onClick={() => handleSuggestionSelect(suggestion)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-start">
                          <svg
                            className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          <span className="text-sm text-gray-900">
                            {suggestion.text}
                          </span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">
                  No suggestions found. You can type your address manually.
                </div>
              )}
            </div>
          )}

          {errors.address && (
            <p className="text-red-500 text-sm mt-1">{errors.address}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Start typing to see address suggestions, or enter manually
          </p>
        </div>

        {/* Suburb, State, Post Code */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Suburb <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.suburb || ""}
              onChange={(e) => updateFormData({ suburb: toTitleCase(e.target.value) })}
              onBlur={() => handleBlur('suburb')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
                errors.suburb ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Tumbulgum"
            />
            {errors.suburb && (
              <p className="text-red-500 text-sm mt-1">{errors.suburb}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.state || ""}
              onChange={(e) => updateFormData({ state: e.target.value })}
              onBlur={() => handleBlur('state')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
                errors.state ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="NSW"
            />
            {errors.state && (
              <p className="text-red-500 text-sm mt-1">{errors.state}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Post Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.postCode || ""}
              onChange={(e) =>
                updateFormData({ postCode: e.target.value.replace(/\D/g, "") })
              }
              onBlur={() => handleBlur('postCode')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
                errors.postCode ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="2490"
              maxLength={4}
            />
            {errors.postCode && (
              <p className="text-red-500 text-sm mt-1">{errors.postCode}</p>
            )}
          </div>
        </div>

        {/* Shirt Size */}
        <div className="w-full md:w-1/2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shirt Size
            </label>
            <select
              value={formData.shirtSize}
              onChange={(e) =>
                updateFormData({ shirtSize: e.target.value as ShirtSize })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent"
            >
              {Object.values(ShirtSize).map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              For club merchandise, when available. This is kept on file for future
              merchandise runs — it doesn't guarantee an order or delivery timeframe.
            </p>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="border-t pt-4 mt-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Emergency Contact (Optional)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Name
              </label>
              <input
                type="text"
                value={formData.emergencyContactName || ""}
                onChange={(e) =>
                  updateFormData({ emergencyContactName: toTitleCase(e.target.value) })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent"
                placeholder="Jane Smith"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Phone
              </label>
              <input
                type="tel"
                value={formData.emergencyContactPhone || ""}
                onChange={(e) =>
                  updateFormData({ emergencyContactPhone: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent"
                placeholder="0412 345 678"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between mt-8">
        <button
          onClick={onCancel}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleNext}
          className="px-6 py-2 bg-fishing-blue hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PersonalDetailsStep;
